import { pageQueryParams } from '@/components/search/pageQueryParams'
import { SortableHeader } from '@/components/search/SortableHeader'
import { groupLabel } from '@/components/usergroup/group'
import './user.css'

import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { ManagerEditUserForm, ManagerInsertUserForm, UserFormData } from './common/forms'
import { CurrentSession, SessionService } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors, onSubmit } from '@/components/forms/common/FormBase'
import { useForm, UseFormReturn } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { formatDate } from '@/common/time'
import { useModal } from '@/components/modal/Modal'
import { BackButton } from '@/components/layout/NavBar'
import { Translates } from '@/services/repository/i18n'
import { useNavigate } from 'react-router-dom'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { useAppContext } from '@/context/app'
import { fetchCSV, fetchExcel } from '@/common/fetch'
import { Spinner } from '@/components/spinner'
import { CreateUserWizardPopup } from './wizard/create/Main'
import { SimpleUserGroup, UserGroupMultiSelect } from '@/components/usergroup/UserGroupMultiSelect'

export interface SimpleUser {
  id: string
  email: string
  username: string
  createdAt: Date
  isRelated?: boolean
}

// export interface ManagerInsertPopupProps {
//   services: Services,
//   session: CurrentSession,
//   onClose: () => void
// }

// export interface ManagerEditUsersPopupProps extends ManagerInsertPopupProps {
//   id: string | undefined
// }

// export interface ManagerInsertUsersPopupProps extends ManagerInsertPopupProps { }

export const ManagerEditUsersPopup = ({ onClose, id }: {
  id: string | undefined
  onClose: () => void
}) => {
  const form = useForm<UserFormData>()
  const [data, setData] = useState<UserFormData>()
  const { services, session, translates } = useAppContext()

  useEffect(() => {
    if (id === undefined) {
      return
    }
    setData(undefined)
    services.session.fetchWithToken(`/api/manager/users/byid/${id}`).then((r) => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [id])

  const handle = (data: UserFormData): Promise<void> => {
    if (form === undefined || !translates) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/manager/users/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const respData = await r.json()
        respData.errors && addServerErrors(form, translates, respData.errors)
        return
      }
      onClose()
    })
  }

  if (!translates || !session) {
    return <Spinner />
  }

  return data !== undefined ? <ManagerEditUserForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={translates}
    formName='user'
    readOnly={false}
  /> : <Spinner />
}

// export const ManagerInsertUsersPopup = ({ services, session, onClose }: ManagerInsertUsersPopupProps) => {
//   const form = useForm<UserFormData>({
//     defaultValues: {
//       lang: 'pl',
//       role: 'user'
//     }
//   })

//   const handle = (data: UserFormData): Promise<void> => {
//     if (form === undefined) {
//       return Promise.reject()
//     }
//     delete data.id
//     return services.session.fetchWithToken(`/api/superadmin/users/new`, {
//       method: 'POST',
//       body: JSON.stringify(data),
//     }).then(async (r) => {
//       if (r.status !== 200) {
//         const erros = await r.json()
//         addServerErrors(form, session.translates, erros)
//         return
//       }
//       form.reset()
//       onClose()
//     })
//   }

//   return <ManagerInsertUserForm
//     onSubmit={handle}
//     services={services}
//     form={form}
//     translates={session.translates}
//     formName='user'
//     readOnly={false}
//   />
// }

// export interface ManagerRemoveUsersPopupProps extends ManagerInsertPopupProps {
//   user: SimpleUser | undefined
// }


// export const ManagerRemoveUsersPopup = ({ services, session, onClose, user }: ManagerRemoveUsersPopupProps) => {
//   const form = useForm<UserFormData>()
//   const [error, setError] = useState<boolean>(false)

//   useEffect(() => {
//     setError(false)
//   }, [user?.id])

//   if (user === undefined) {
//     return null
//   }

//   const remove = (): Promise<void> => {
//     return services.session.fetchWithToken(`/api/superadmin/users/byid/${user.id}/delete`, {
//       method: 'DELETE'
//     }).then(async (r) => {
//       if (r.status !== 200) {
//         setError(true)
//       }
//       setError(false)
//       onClose()
//     })
//   }
//   return <div className='dialog'>
//     <div className='dialog-text'>{session.translates.for('elements.table.users.remove_user_ask').replace('{username}', user.username)}</div>
//     <div className='dialog-btns'>
//       <div className='dialog-btn' onClick={() => onClose()}>
//         <i className='svgfont-no' />
//         {session.translates.for('elements.dialog.yesno.no')}
//       </div>
//       <div className='dialog-btn' onClick={() => remove()}>
//         <i className='svgfont-yes' />
//         {session.translates.for('elements.dialog.yesno.yes')}
//       </div>
//       {error ? <div className='dialog-error'>{session.translates.for('elements.table.users.remove_user_error')}</div> : null}
//     </div>
//   </div>
// }


// --- RELATION TO USER GROUPS -----

interface SimpleRelatedUserGroup {
  id: string
  name: string
  isRelated?: boolean
}

const RelatedUserGroupsPage = ({
  userId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  userId: string | undefined,
  onClose: () => void
}) => {
  const { services, session, translates } = useAppContext()
  const searchTableRef = useRef<SearchTableRef>(null)

  if (!translates || !session) {
    return <Spinner />
  }

  const tableParams: SearchTableParams<SimpleRelatedUserGroup> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleRelatedUserGroup>> => {
      return services.session.fetchWithToken(
        `/api/manager/users/byid/${userId}/groups/search?${pageQueryParams(q)}`
      ).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 5,
      search: ""
    },
    toolbox: () => <></>,
    defaultSortBy: 'name',
    header: (query, onSort) => <>
      <div className='hcol'></div>
      <SortableHeader field='name' query={query} onSort={onSort}>{session?.translates.for('elements.table.usergroups.name')}</SortableHeader>
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState(state => {
          if (state === undefined) return
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        services.session.fetchWithToken(`/api/manager/users/byid/${userId}/relations/groups/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then(resp => {
          setState(state => {
            if (state === undefined) return
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        })
      }}>
        {row.isRelated === undefined
          ? <div className='spinner'></div>
          : row.isRelated
            ? <i className='svgfont-checkbox' />
            : <i className='svgfont-checkbox-blank' />
        }
      </div>
      <div className='col'>{groupLabel(row)}</div>
    </>,
    translates: translates
  }

  return <SearchTable ref={searchTableRef} {...tableParams} />
}

const ExportPopup = ({ onClose, selectedGroupIds }: {
  onClose: () => void
  selectedGroupIds: string[]
}) => {
  const { services, session } = useAppContext()

  const groupsQuery = new URLSearchParams({ groups: selectedGroupIds.join(',') }).toString()

  const menu: Menu = {
    title: "popup.export.users_export_title",
    rows: [
      {
        title: 'popup.export.excel_export',
        action: () => fetchExcel({
          services,
          url: `/api/manager/users/excel?${groupsQuery}`,
          fileName: 'users.xlsx'
        })
      },
      {
        title: 'popup.export.csv_export',
        action: () => fetchCSV({
          services,
          url: `/api/manager/users/csv?${groupsQuery}`,
          fileName: 'users.csv'
        })
      }
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const ManagerUserMenuPopup = ({ services, session, translates, onClose, row }: {
  translates: Translates,
  services: Services,
  session: CurrentSession,
  onClose: () => void
  row: SimpleUser
}) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const menu: Menu = {
    title: `${row.username}`,
    rows: [
      // {
      //   icon: 'svgfont-password',
      //   title: 'elements.table.users.menu.edit.password',
      //   action: () => openModal((props) => ManagerNewPasswordPopup({ ...props, id: row.id, services, session })),
      // },
      {
        icon: 'svgfont-users',
        title: 'elements.table.users.menu.edit.groups',
        action: () => openModal((props) => {
          return RelatedUserGroupsPage({ ...props, userId: row.id })
        }),
      },
      {
        icon: 'svgfont-edit',
        title: 'elements.table.users.menu.edit.edit_row',
        action: () => openModal((props) => {
          return ManagerEditUsersPopup({ ...props, id: row.id })
        }),
      },
      {
        icon: 'svgfont-nfc',
        title: 'elements.table.users.menu.edit.edit_cards',
        action: () => navigate(`/communal/cards/manager/foruser/${row.username}`),
      },
      // {
      //   icon: 'svgfont-remove',
      //   title: 'elements.table.users.menu.edit.remove',
      //   action: () => openModal((props) => ManagerRemoveUsersPopup({ ...props, user: row, services, session })),
      // },
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const ManagerUsersPage = () => {
  const { services, session } = useAppContext()
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()
  const [usergroups, setUsergroups] = useState<SimpleUserGroup[]>()
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([])

  function refresh() {
    services.session.fetchWithToken(`/api/communal/usergroups/my`).then((response) => {
      return response.json()
    }).then((data) => {
      setUsergroups(data || [])
    })
  }

  useEffect(refresh, [])

  if (!session || !usergroups) {
    return <Spinner />
  }

  const tableParams: SearchTableParams<SimpleUser> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
      const searchParams = new URLSearchParams({
        pageNumber: q.pageNumber.toString() || '',
        pageSize: q.pageSize.toString() || '',
        search: q.search || '',
        sortBy: q.sortBy || '',
        sortDirection: q.sortDirection || '',
        groups: selectedGroupIds.join(',')
      });
      return services.session.fetchWithToken(`/api/manager/users/search?${searchParams.toString()}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => CreateUserWizardPopup({ ...props }))}><i className='svgfont-add' /></div>
      <div className='tool' onClick={() => openModal((props) => ExportPopup({ ...props, selectedGroupIds }))}>
        <img src="/public/img/icons/export-file.svg" />
      </div>
    </>,
    defaultSortBy: 'createdAt',
    header: (query, onSort) => <>
      <SortableHeader field='createdAt' query={query} onSort={onSort} className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</SortableHeader>
      {/* <div className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</div> */}
      <SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.users.username')}</SortableHeader>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
      {/* <div className='col desktop-only'>{row.email}</div> */}
      <div className='col'>{row.username}</div>
      <div className='col icocol' onClick={() => openModal((props) => ManagerUserMenuPopup({ ...props, row: row, services, session, translates: session.translates }))}>
        <i className='svgfont-menu' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='superadmin-users' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>

    <UserGroupMultiSelect groups={usergroups || []} onChange={setSelectedGroupIds} selectedIds={selectedGroupIds} />
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}
